import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, type SubmitHandler } from "react-hook-form";
import { API_URL } from "../../services/api";
import InputField from "../../components/ui/InputField";
import Button from "../../components/ui/Button";
import { BsGoogle } from "react-icons/bs";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { useAuth } from "../../hooks/useAuth";

import Logo from "../../components/ui/Logo";

interface LoginFormData {
    email: string;
    password: string;
}

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { register, handleSubmit, formState: { errors }, setError } = useForm<LoginFormData>({
        defaultValues: {
            email: "",
            password: "",
        },
    });

    const onSubmit: SubmitHandler<LoginFormData> = async (data) => {
        setIsLoading(true);
        try {
            await login(data.email, data.password);
            
            // Note: Our modified AuthContext login doesn't return the user object directly,
            // we have to check localStorage since it was just set inside login()
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            if (storedUser.isAdmin) {
                navigate("/admin");
            } else {
                navigate("/dashboard");
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Login failed";
            if (errorMessage.includes('Invalid credentials') || errorMessage.includes('Login failed')) {
                setError("root", {
                    message: "The username/email ID does not exist or password is incorrect. Please check your credentials.",
                });
            } else {
                setError("root", {
                    message: errorMessage,
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-linear-to-br from-[#F7D4E9] to-[#E2E6FF] px-4 py-8 grid place-items-center">
            <div className="w-full max-w-6xl mx-auto flex flex-col md:flex-row gap-8 px-4 md:px-6 md:h-[95%]">
                <div className="w-full md:w-1/2 h-[200px] md:h-auto rounded-xl shadow-lg border border-white/20 overflow-hidden bg-white/10 backdrop-blur-xs">
                    <img src="/images/auth-hero.png" alt="Hero" className="w-full h-full object-cover" />
                </div>
                <div className="w-full md:w-1/2 max-w-lg flex flex-col justify-center space-y-6 mx-auto md:mx-0">
                    <Logo />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Log in to your account</h1>
                        <p className="text-gray-500 mt-1.5 text-sm">Welcome back! Select method to log in:</p>
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <InputField
                            type="email"
                            placeholder="Email"
                            variant="outline"
                            disabled={isLoading}
                            {...register("email", { required: "Email is required" })}
                            error={errors.email?.message}
                            className="focus:border-purple-600 bg-transparent!"
                        />
                        <div className="relative">
                            <InputField
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter your password..."
                                variant="outline"
                                disabled={isLoading}
                                {...register("password", { required: "Password is required" })}
                                error={errors.password?.message}
                                className="focus:border-purple-600 bg-transparent! pr-11"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-[11px] text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                                tabIndex={-1}
                            >
                                {showPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                            </button>
                        </div>
                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input type="checkbox" id="rememberMe" className="w-4 h-4" />
                                <span>Remember me</span>
                            </label>
                            <a className="text-sm text-purple-600 hover:underline" href="/reset-password">Forgot password?</a>
                        </div>

                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full mt-2"
                        >
                            {isLoading ? "Logging in..." : "Get Started"}
                        </Button>
                        {errors.root && (
                            <div className="bg-red-50 border border-red-200 rounded-md p-3 mt-2">
                                <p className="text-red-600 text-sm">{errors.root.message}</p>
                            </div>
                        )}
                    </form>

                    <div className="relative max-w-md mx-auto w-full">
                        <hr className="my-3 border-gray-300" />
                        <p className="absolute top-0.5 left-1/2 -translate-x-1/2 bg-[#E5E0FF] px-3 text-center text-sm text-gray-500">or register with</p>
                    </div>

                    <Button
                        variant="outline"
                        className="w-full bg-white flex items-center justify-center gap-3"
                        onClick={() => {
                            window.location.href = `${API_URL}/api/auth/google`;
                        }}
                    >
                        <BsGoogle className="text-lg" />
                        <span>Google</span>
                    </Button>

                    <p className="text-center text-sm">Don't have an account? <a href="/signup" className="text-purple-600">Sign up</a> </p>
                </div>
            </div>
        </div>
    );
}